import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { TutorialVideosFacade } from 'gb-dashboard/staff/data-access-tutorial-videos'
import * as TutorialVideosActions from 'libs/gb-dashboard/staff/data-access-tutorial-videos/src/lib/+state/tutorial-videos.actions'
import { NavigationEnd, Router } from '@angular/router'
import { filter, map, startWith } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { matchesPattern } from './gb-tutorial-popup-video.utils'
import { HelpConstants } from 'libs/gb-dashboard/help/feature/src/lib/help.constants'

enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5,
}

export interface TutorialVideo {
  videoId: string
  title: string
}

@UntilDestroy()
@Component({
  selector: 'gb-tutorial-popup-video',
  templateUrl: './gb-tutorial-popup-video.component.html',
  styleUrls: ['./gb-tutorial-popup-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GbTutorialPopupVideoComponent implements OnInit {
  playerState: PlayerState = PlayerState.UNSTARTED
  playerVars: any = {
    iv_load_policy: 3, // Turns off annotations when video ends
    modestbranding: 1, // Removes YouTube logo
    rel: 0, // Disables suggested videos
  }
  playingStarted = false
  currentVideoIndex = 0

  get isPlaying() {
    return this.playerState === PlayerState.PLAYING
  }

  get isPaused() {
    return this.playerState === PlayerState.PAUSED
  }

  get isEnded() {
    return this.playerState === PlayerState.ENDED
  }

  get currentVideo() {
    return this.videos[this.currentVideoIndex]
  }

  get videos(): TutorialVideo[] {
    if (Array.isArray(this.data.videos)) {
      return this.data.videos.slice(0, 3)
    } else if (this.data.videoId) {
      return [
        {
          videoId: this.data.videoId,
          title: 'Tutorial Video',
        },
      ]
    }
    return []
  }

  get hasMultipleVideos() {
    return this.videos.length > 1
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      videoId?: string
      videos?: TutorialVideo[]
      target: string
    },
    private el: ElementRef,
    private renderer: Renderer2,
    private tutorialVideosFacade: TutorialVideosFacade,
    private router: Router,
  ) {}

  ngOnInit() {
    // If the route is different from video's target route, close the dialog
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.url),
        startWith(this.router.url),
        untilDestroyed(this),
      )
      .subscribe((url) => {
        if (!matchesPattern(url, this.data.target.replace('/auction/', '/campaign/'))) {
          this.tutorialVideosFacade.dispatch(TutorialVideosActions.hideTutorialPopUpVideo())
        }
      })
  }

  selectVideo(index: number) {
    if (index !== this.currentVideoIndex && index >= 0 && index < this.videos.length) {
      this.currentVideoIndex = index
      this.playerState = PlayerState.UNSTARTED
      this.playingStarted = false
    }
  }

  onPlayerStateChange(event: any) {
    this.playerState = event.data
    if (this.isPlaying && !this.playingStarted) {
      this.playingStarted = true
    }
  }

  dismiss() {
    const container = this.el.nativeElement.querySelector('.tutorial-popup-container')
    this.renderer.addClass(container, 'dismiss')
    this.tutorialVideosFacade.dispatch(TutorialVideosActions.hideTutorialPopUpVideo())
  }

  goToHelpCenter(): void {
    this.router.navigate([`../${HelpConstants.HELP}`])
  }
}
